import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CircleDot, Clock, History, Info, Link2, Lock, RotateCcw, ShieldCheck, Ticket, Trophy, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import {
  fetchRaffleData,
  fetchRaffleSchedule,
  type RaffleScheduleDraw,
} from '../../features/wallet/api';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { ticketLabel } from '../../shared/raffle/ticketSerial';
import { readStorage, writeStorage } from '../../shared/lib/storage';
import { countWinners, poolOf, prizeRows, type PrizeRow } from '../../shared/game/rafflePrize';
import { fmtMoney, fmtInt } from '../../shared/lib/format';
import { currentUnixSeconds, formatRaffleCountdown, raffleSecondsRemaining, serverEpochSeconds } from '../../shared/lib/raffleCountdown';
import { raffleHeroPhase, raffleScheduleChipKind, type RaffleScheduleChipKind } from '../../shared/lib/raffleScheduleStatus';
import { localDayKey } from '../../shared/lib/datetime';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { GuestAuthSheet } from '../../features/auth/GuestAuthSheet';

/**
 * Draw clocks in the VIEWER's own timezone (product decision 2026-09-10).
 *
 * These used to render in `America/Sao_Paulo` so the board would match the
 * operator's fixed Brazilian schedule. On any phone outside UTC-3 that put an
 * absolute time on screen that contradicted the countdown beside it — a phone
 * reading 18:27 was told "08:00 · open · draws in 32m". The countdown was always
 * right (it runs on epoch seconds); the clock was the lie. Now both come from
 * the same instant in the same zone, so they agree by construction.
 *
 * `hour12: false` and no explicit locale: the app's other clocks do the same, and
 * an es-AR or en device must not get a Brazilian format here.
 */
function hhmm(sec: number | undefined) {
  if (!sec) return '--:--';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(sec * 1000));
  } catch {
    return '--:--';
  }
}

// "Today" and the AMANHÃ tag are day headings, so they follow the viewer's own
// calendar day — same rule as the archive and My Bets (decision rule 4).
function isToday(sec: number, nowSec: number) {
  return localDayKey(sec) === localDayKey(nowSec);
}

// The "your last result" recap is a transient welcome-back nudge, not a fixture:
// it only shows for a recently-resolved draw, and a dismiss ✕ hides it for good.
const RECAP_WINDOW_SEC = 24 * 3600;
const RECAP_DISMISS_KEY = 'sorteia:raffleRecapDismissed';

function readRecapDismissed(): Set<string> {
  try {
    const raw = readStorage(localStorage, RECAP_DISMISS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function persistRecapDismissed(ids: Set<string>) {
  // Keep the list bounded — only the most recent dismissals matter.
  writeStorage(localStorage, RECAP_DISMISS_KEY, JSON.stringify([...ids].slice(-40)));
}

// A draw whose registration window hasn't opened yet is, by definition, still
// upcoming — it cannot have a result. Guard against a stale/erroneous terminal
// status (voided/settled) leaking onto a future slot, which would show an
// impossible "Voided" before an earlier draw has even opened.
function drawKind(draw: RaffleScheduleDraw, nowSec: number): RaffleScheduleChipKind {
  return raffleScheduleChipKind(draw.status, Number(draw.registration_starts || 0), nowSec, Number(draw.scheduled_at || 0));
}

function sameEventId(a: unknown, b: unknown) {
  const left = String(a ?? '').trim();
  const right = String(b ?? '').trim();
  return left !== '' && left === right;
}

export function SorteiosPage() {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const user = useAuthStore((state) => state.user);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const logout = useAuthStore((state) => state.logout);
  const viewer = raffleViewerMode(token, guestMode);
  const isGuest = viewer === 'guest';
  const [guestAuthOpen, setGuestAuthOpen] = useState(false);
  // A guest who taps "participate" / "earn" leaves guest mode for the auth
  // screens — the same exit the History gate used to offer.
  const leaveGuest = (nextRoute: 'auth.register' | 'auth.login') => {
    clearGuest();
    logout();
    setRoute(nextRoute);
  };
  const setRoute = useAppStore((state) => state.setRoute);
  const openWalletTicketsFlow = useAppStore((state) => state.openWalletTicketsFlow);
  const [deviceNow, setDeviceNow] = useState(currentUnixSeconds);
  const [clockOffset, setClockOffset] = useState(0);
  const [showHow, setShowHow] = useState(false);
  const [recapDismissed, setRecapDismissed] = useState<Set<string>>(readRecapDismissed);

  useEffect(() => {
    const id = window.setInterval(() => setDeviceNow(currentUnixSeconds()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token || ''),
    enabled: raffleReadsEnabled(viewer),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const scheduleQuery = useQuery({
    queryKey: ['raffle-schedule', token],
    queryFn: () => fetchRaffleSchedule(token || ''),
    enabled: raffleReadsEnabled(viewer),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Run countdowns on the server clock (device time + server offset), so a
  // phone with a wrong clock or a non-Brazil timezone still shows correct time.
  const serverTime = serverEpochSeconds(raffleQuery.data?.server_time) || serverEpochSeconds(scheduleQuery.data?.server_time);
  useEffect(() => {
    if (serverTime) setClockOffset(serverTime - currentUnixSeconds());
  }, [serverTime]);
  const nowSec = deviceNow + clockOffset;

  const event = raffleQuery.data?.event || null;
  const tickets = raffleQuery.data?.tickets || [];
  const activeTickets = tickets.filter((ticket) => (ticket.status || 'active') === 'active').length;
  const draws = (scheduleQuery.data?.draws || []).filter((d) => isToday(Number(d.scheduled_at), nowSec) || Number(d.scheduled_at) > nowSec);

  // The most-actionable draw (open first, else the next upcoming) anchors the
  // horizontal board — scroll it fully into view so it's never clipped past the
  // edge behind already-finished draws.
  const anchorIdx = (() => {
    const openI = draws.findIndex((d) => drawKind(d, nowSec) === 'open');
    if (openI >= 0) return openI;
    const soonI = draws.findIndex((d) => Number(d.scheduled_at) > nowSec);
    return soonI >= 0 ? soonI : 0;
  })();
  const rowRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const nextDrawRef = useRef<HTMLButtonElement | null>(null);
  const anchorKey = draws[anchorIdx]?.scheduled_at;
  useEffect(() => {
    const row = rowRef.current;
    const chip = anchorRef.current;
    if (!row || !chip) return;
    row.scrollTo({ left: Math.max(0, chip.offsetLeft - 14), behavior: 'auto' });
  }, [anchorKey, draws.length]);

  // Returning-user headline: "did I win my last draw?" — the most recent draw
  // this user actually entered that has already resolved (settled or voided).
  const recentEvents = raffleQuery.data?.recent_events || [];
  const myLastResult = recentEvents
    .filter((ev) => {
      const s = (ev.status || '').toLowerCase();
      const mine = (ev.my_entries?.length || 0) > 0 || Number(ev.my_prize || 0) > 0;
      return mine && (s === 'settled' || s === 'paid' || s === 'voided');
    })
    .sort((a, b) => Number(b.scheduled_at || 0) - Number(a.scheduled_at || 0))[0] || null;
  const lastWon = !!myLastResult && Number(myLastResult.my_prize || 0) > 0;
  const lastVoided = !!myLastResult && (myLastResult.status || '').toLowerCase() === 'voided';
  const lastResultId = myLastResult ? String(myLastResult.event_id || myLastResult.id || myLastResult.scheduled_at) : '';
  const recapRecent = !!myLastResult && nowSec - Number(myLastResult.scheduled_at || 0) < RECAP_WINDOW_SEC;
  const showRecap = !!myLastResult && recapRecent && !recapDismissed.has(lastResultId);
  const dismissRecap = () => {
    const next = new Set(recapDismissed);
    next.add(lastResultId);
    persistRecapDismissed(next);
    setRecapDismissed(next);
  };

  const openRegister = () => {
    if (isGuest) { leaveGuest('auth.register'); return; }
    openWalletTicketsFlow('register', null, { route: 'sorteios' });
  };
  const openResult = (eventId?: string | null) => openWalletTicketsFlow('result', eventId || null, { route: 'sorteios' });
  const openParticipants = () => openWalletTicketsFlow('participants', null, { route: 'sorteios' });
  const goEarn = () => {
    if (isGuest) { setGuestAuthOpen(true); return; }
    setRoute('rewards');
  };

  const currentEventId = String(event?.event_id ?? event?.id ?? '');
  // The participants sheet always renders the *current* event, so only the chip
  // that actually is that event may open it — otherwise a later locked slot
  // would show someone else's entrant list.
  const isCurrentDraw = (draw: RaffleScheduleDraw) => sameEventId(draw.event_id, currentEventId);

  const maxEntries = Number(event?.max_total_entries || 0);
  const totalEntries = Number(event?.total_entries || 0);
  const minEntries = Number(event?.min_entries_to_run || 0);
  const pct = maxEntries > 0 ? Math.min(100, Math.round((totalEntries / maxEntries) * 100)) : 0;
  const minPct = maxEntries > 0 && minEntries > 0 ? Math.min(100, Math.round((minEntries / maxEntries) * 100)) : 0;
  const fillPct = totalEntries > 0 ? Math.max(4, pct) : 0;
  const minReached = minEntries > 0 && totalEntries >= minEntries;
  const winnersCount = countWinners(event?.prize_brackets);
  const pool = poolOf(event?.prize_brackets);
  const prizeList = prizeRows(event?.prize_brackets);
  const secsLeft = event ? raffleSecondsRemaining(event.scheduled_at, nowSec) : 0;

  // The hero is a state machine, not an open/absent switch: a draw that filled
  // early is still this page's headline — it has a pool, a countdown, and for
  // most players their own numbers in it.
  const heroPhase = event
    ? raffleHeroPhase({
      status: event.status || '',
      totalEntries,
      maxTotalEntries: maxEntries,
      scheduledAt: Number(event.scheduled_at || 0),
      nowSec,
    })
    : 'none';
  const held = heroPhase === 'full' || heroPhase === 'drawing';

  // "Am I in this one?" — the first question a player has once entries close.
  const myEntryCount = (raffleQuery.data?.entries || []).filter(
    (entry) => sameEventId(entry.user_id, user?.id) && sameEventId(entry.event_id, currentEventId),
  ).length;

  // The only useful exit for someone who did not get in.
  const nextDrawIdx = draws.findIndex((draw) => Number(draw.scheduled_at) > Number(event?.scheduled_at || nowSec));
  const nextDraw = nextDrawIdx >= 0 ? draws[nextDrawIdx] : null;
  const goNextDraw = () => {
    const row = rowRef.current;
    const chip = nextDrawRef.current;
    if (row && chip) row.scrollTo({ left: Math.max(0, chip.offsetLeft - 14), behavior: 'smooth' });
  };

  function onChip(draw: RaffleScheduleDraw) {
    const kind = drawKind(draw, nowSec);
    if (kind === 'open') openRegister();
    else if (kind === 'closed' || kind === 'voided') openResult(draw.event_id);
    else if ((kind === 'full' || kind === 'drawing') && isCurrentDraw(draw)) openParticipants();
  }

  return (
    <section className="srt-page">
      {/* 用设计系统的 ds-header，不要 srt-head/srt-icon。
          那是这一页自己复制的一套：34×34 的圆形图标按钮，低于 D4 明令的
          「触达区 ≥ 44px」，而且让同一个 App 里的返回键长得不一样。
          这里删的是一份重复，不是再加一种风格（评审 2026-08-19 §1）。 */}
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button"
          onClick={() => setRoute('lobby')} aria-label={t('backToLobby')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">{t('sorteiosTitle')}</h1>
        {/* 原先这里是 (i)，和页面底部那张「Como funciona」卡调的是同一个
            setShowHow —— 两个入口同屏可见，是重复。删掉页头这个，槽位给
            History；底部那张留着，它挨着内容，是内容层的延伸。
            外形与 Milhar 页头的 Resultados 药丸一致（.milhar-head-results）：
            图标青色不用金色，金色是钱与主 CTA 专用。 */}
        <button className="milhar-head-results tap" type="button"
          onClick={() => setRoute('sorteios.history')}>
          <History size={15} aria-hidden="true" />
          <span>{t('sorteiosHistoryTitle')}</span>
        </button>
      </header>

      <div className="srt-body">
        {/* Agenda */}
        <section className="srt-sched">
          <div className="srt-label">{t('sorteiosAgenda')}</div>
          {scheduleQuery.isLoading ? (
            <div className="srt-sched-row"><span className="srt-skel" /><span className="srt-skel" /><span className="srt-skel" /></div>
          ) : draws.length === 0 ? (
            <p className="srt-empty">{t('sorteiosNoDraws')}</p>
          ) : (
            <div className="srt-sched-row" ref={rowRef}>
              {draws.map((draw, i) => {
                const kind = drawKind(draw, nowSec);
                const tomorrow = !isToday(Number(draw.scheduled_at), nowSec);
                return (
                  <button
                    key={`${draw.scheduled_at}-${i}`}
                    ref={(node) => {
                      // One chip can be both the anchor and the next draw — a
                      // ternary would silently drop the initial scroll-into-view.
                      if (i === anchorIdx) anchorRef.current = node;
                      if (i === nextDrawIdx) nextDrawRef.current = node;
                    }}
                    type="button"
                    className={`srt-chip srt-chip-${kind}${i === anchorIdx ? ' is-anchor' : ''}`}
                    onClick={() => onChip(draw)}
                    disabled={
                      kind === 'soon'
                      || kind === 'unavailable'
                      || ((kind === 'full' || kind === 'drawing') && !isCurrentDraw(draw))
                    }
                  >
                    {tomorrow ? <span className="srt-chip-day">{t('sorteiosTomorrowTag')}</span> : null}
                    <span className="srt-chip-t">{hhmm(draw.scheduled_at)}</span>
                    <span className="srt-chip-st"><i />{statusLabel(kind, draw, t)}</span>
                    <span className="srt-chip-pool">{fmtMoney(Number(draw.pool || 0))}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Returning user's first question: did I win my last draw? Transient —
            only recent results, and dismissable so it never becomes a fixture. */}
        {showRecap && myLastResult ? (
          <div className={`srt-last-wrap${lastWon ? ' is-won' : lastVoided ? ' is-void' : ''}`}>
            <button
              className="srt-last"
              type="button"
              onClick={() => openResult(lastResultId)}
            >
              <span className="srt-last-ic">
                {lastWon ? <Trophy size={17} aria-hidden="true" /> : lastVoided ? <RotateCcw size={17} aria-hidden="true" /> : <CircleDot size={17} aria-hidden="true" />}
              </span>
              <span className="srt-last-txt">
                <b>{lastWon
                  ? t('sorteiosLastWon').replace('%v%', fmtMoney(myLastResult.my_prize))
                  : lastVoided
                    ? t('sorteiosLastVoided')
                    : t('sorteiosLastNone')}</b>
                <small>{t('sorteiosLastSub').replace('%t%', hhmm(myLastResult.scheduled_at))}</small>
              </span>
            </button>
            <button className="srt-last-x" type="button" onClick={dismissRecap} aria-label={t('close')}>
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        ) : null}

        {/* Hero — current open draw. Participate is always the primary action. */}
        {event && heroPhase === 'open' ? (
          <section className="srt-hero">
            <div className="srt-hero-top">
              <div>
                <div className="srt-hero-lab">{hhmm(event.scheduled_at)} · {t('sorteiosPoolLabel')}</div>
                <div className="srt-hero-pool">{pool > 0 ? fmtMoney(pool) : t('sorteiosPoolTbd')}</div>
              </div>
              <span className={`srt-cd${secsLeft > 0 && secsLeft <= 30 ? ' is-urgent' : ''}`}>
                <Clock size={13} aria-hidden="true" />
                <em className="srt-cd-lab">{t('sorteiosDrawsIn')}</em>
                <b>{heroCountdown(secsLeft, event.scheduled_at, nowSec)}</b>
              </span>
            </div>
            <PrizeBreakdown prizes={prizeList} winners={winnersCount} pool={pool} />
            {/* The whole entries block is the door to the participants list —
                who's in, and my numbers — instead of a separate row below. */}
            <button
              type="button"
              className="srt-meter-btn"
              onClick={() => openWalletTicketsFlow('participants', null, { route: 'sorteios' })}
              aria-label={t('sorteiosViewNumbers')}
            >
              <div className="srt-meter-line">
                <span>{t('sorteiosEntriesLabel')}</span>
                <b>{fmtInt(totalEntries)} / {fmtInt(maxEntries)}<ChevronRight size={13} aria-hidden="true" /></b>
              </div>
              <div className="srt-meter">
                <i className={minReached ? 'is-ok' : ''} style={{ width: `${fillPct}%` }} />
                {minPct > 0 && minPct < 100 ? <span className="srt-meter-min" style={{ left: `${minPct}%` }} aria-hidden="true" /> : null}
              </div>
              <div className={`srt-meter-hint${minReached ? ' is-ok' : ''}`}>
                {minEntries > 0 && totalEntries < minEntries
                  ? t('sorteiosMinToRun').replace('%n%', fmtInt(minEntries))
                  : t('sorteiosMinReached')}
              </div>
            </button>
            {/* One ticket line: cost context on the left, the earn entry on the
                right — replaces the separate "N tickets · Earn more" row below. */}
            <button className="srt-mine is-link" type="button" onClick={goEarn}>
              <span>
                {activeTickets > 0
                  ? t('sorteiosMyTicketsFree').replace('%n%', fmtInt(activeTickets))
                  : t('sorteiosFreeOdds').replace('%n%', fmtInt(winnersCount))}
              </span>
              <em>{t('sorteiosEarnMore')}<ChevronRight size={13} aria-hidden="true" /></em>
            </button>
            <button className="srt-cta-gold" type="button" onClick={openRegister}>
              {isGuest ? t('sorteiosGuestCta') : t('sorteiosParticipate')}
            </button>
          </section>
        ) : event && held ? (
          /* Entries are closed but the draw has not resolved. This used to render
             nothing at all — the pool, the countdown and the player's own numbers
             vanished the second the last seat sold, which read as a broken page.
             Everything stays; only the invitation changes. */
          <section className={`srt-hero is-held is-${heroPhase}`}>
            <div className={`srt-hero-flag is-${heroPhase}`}>
              {heroPhase === 'full'
                ? <Lock size={13} aria-hidden="true" />
                : <CircleDot size={13} aria-hidden="true" />}
              <span>{heroPhase === 'full' ? t('sorteiosFullFlag') : t('sorteiosDrawingFlag')}</span>
            </div>
            <div className="srt-hero-top">
              <div>
                <div className="srt-hero-lab">{hhmm(event.scheduled_at)} · {t('sorteiosPoolLabel')}</div>
                <div className="srt-hero-pool">{pool > 0 ? fmtMoney(pool) : t('sorteiosPoolTbd')}</div>
              </div>
              {heroPhase === 'full' ? (
                <span className={`srt-cd${secsLeft > 0 && secsLeft <= 30 ? ' is-urgent' : ''}`}>
                  <Clock size={13} aria-hidden="true" />
                  <em className="srt-cd-lab">{t('sorteiosDrawsIn')}</em>
                  <b>{heroCountdown(secsLeft, event.scheduled_at, nowSec)}</b>
                </span>
              ) : (
                <span className="srt-cd is-live">
                  <b>{t('sorteiosResultSoon')}</b>
                </span>
              )}
            </div>
            <PrizeBreakdown prizes={prizeList} winners={winnersCount} pool={pool} />
            <button
              type="button"
              className="srt-meter-btn"
              onClick={openParticipants}
              aria-label={t('sorteiosViewNumbers')}
            >
              <div className="srt-meter-line">
                <span>{t('sorteiosEntriesLabel')}</span>
                <b>{fmtInt(totalEntries)} / {fmtInt(maxEntries)}<ChevronRight size={13} aria-hidden="true" /></b>
              </div>
              <div className="srt-meter">
                <i className="is-ok" style={{ width: '100%' }} />
              </div>
              <div className="srt-meter-hint is-ok">
                {t('sorteiosAllSeatsTaken').replace('%n%', fmtInt(maxEntries))}
              </div>
            </button>
            {/* Whether the player is in decides everything below: never resell a
                draw someone already holds numbers in, and never send someone who
                missed it to earn a ticket for a draw that cannot use it. */}
            {myEntryCount > 0 ? (
              <div className="srt-mine is-in">
                <Ticket size={14} aria-hidden="true" />
                <span>{myEntryCount === 1
                  ? t('sorteiosImInOne')
                  : t('sorteiosImIn').replace('%n%', fmtInt(myEntryCount))}</span>
              </div>
            ) : (
              <button className="srt-mine is-link" type="button" onClick={goEarn}>
                <span>{activeTickets > 0
                  ? t('sorteiosTicketsKeep').replace('%n%', fmtInt(activeTickets))
                  : t('sorteiosNotIn')}</span>
                <em>{t('sorteiosEarnMore')}<ChevronRight size={13} aria-hidden="true" /></em>
              </button>
            )}
            {myEntryCount > 0 ? (
              <button className="srt-cta-signal" type="button" onClick={openParticipants}>
                {t('sorteiosSeeMyNumbers')}
              </button>
            ) : (
              <button className="srt-cta-ghost" type="button" onClick={nextDraw ? goNextDraw : undefined} disabled={!nextDraw}>
                {nextDraw
                  ? t('sorteiosNextDrawAt').replace('%t%', hhmm(nextDraw.scheduled_at))
                  : t('sorteiosNextDrawUnknown')}
              </button>
            )}
          </section>
        ) : raffleQuery.isError || scheduleQuery.isError ? (
          <p className="srt-empty">{t('sorteiosLoadError')}</p>
        ) : !raffleQuery.isLoading ? (
          <p className="srt-empty srt-empty-hero">{t('sorteiosEmptyHero')}</p>
        ) : null}

        {/* Prizes + how it works + fairness — one focused sheet, not the dense rules page */}
        <button className="srt-about" type="button" onClick={() => setShowHow(true)}>
          <span className="srt-about-ic is-cyan"><Info size={15} aria-hidden="true" /></span>
          <b>{t('sorteiosHowRow')}</b>
          <ChevronRight size={15} className="srt-about-cv" aria-hidden="true" />
        </button>

      </div>

      {showHow ? (
        <HowItWorksSheet
          onClose={() => setShowHow(false)}
          onVerify={() => { setShowHow(false); setRoute('rules'); }}
          onEarn={() => { setShowHow(false); goEarn(); }}
        />
      ) : null}

      {guestAuthOpen ? (
        <GuestAuthSheet
          onSignup={() => { setGuestAuthOpen(false); leaveGuest('auth.register'); }}
          onLogin={() => { setGuestAuthOpen(false); leaveGuest('auth.login'); }}
          onClose={() => setGuestAuthOpen(false)}
        />
      ) : null}
    </section>
  );
}

// What winning looks like, on the card itself: every prize tier and how many
// people take it. It used to hide behind a "Top prize R$ 50 · 14 winners ›"
// link — the one thing that makes a free draw worth a tap was one tap away.
function PrizeBreakdown({ prizes, winners, pool }: { prizes: PrizeRow[]; winners: number; pool: number }) {
  const t = useI18nStore((state) => state.t);
  if (prizes.length === 0) return null;
  return (
    <div className="srt-hero-prizes">
      <ol className="srt-how-prize-list">
        {prizes.map((row, i) => (
          <li key={i} className={i === 0 ? 'is-top' : ''}>
            <span className="srt-how-prize-who">
              {i === 0 ? <Trophy size={14} aria-hidden="true" /> : null}
              {i === 0 && row.count === 1
                ? t('sorteiosHowPrizeTop')
                : t('sorteiosHowPrizeRow').replace('%n%', fmtInt(row.count))}
            </span>
            <b>{fmtMoney(row.amount)}</b>
          </li>
        ))}
      </ol>
      <div className="srt-how-prize-total">
        <span>{t('sorteiosHowTotal').replace('%n%', fmtInt(winners))}</span>
        <b>{fmtMoney(pool)}</b>
      </div>
    </div>
  );
}

// Focused "how the raffle works + fairness" sheet. Replaces dumping a
// raffle player into the dense betting-games rules page; the on-chain math is
// still one tap away for skeptics via onVerify.
function HowItWorksSheet({
  onClose,
  onVerify,
  onEarn,
}: {
  onClose: () => void;
  onVerify: () => void;
  onEarn: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  // Four illustrated steps, one line each — the real algorithm from
  // JdbcRaffleRepository.winnerSerials. Worked example: D = 1962322286,
  // N = 14, so 1962322286 mod 14 = 8 and the top prize is ticket 008.
  //
  // It read "1st = #009" until 2026-09-25, left over from the 1-based era, and
  // pointed at SorteioFairnessPage, which has since been retired.
  const exD = '1962322286';
  const exN = 14;
  const exRem = 8;
  // 中奖号就是余数本身——票号 0 起，没有 +1 那一步（2026-09-24 PM）。
  const exFirst = exRem;
  const cell = (n: number, cls: string, tag?: string) => (
    <span key={n} className={`srt-how-cell ${cls}`}>
      {tag ? <em>{tag}</em> : null}
      {ticketLabel(n)}
    </span>
  );
  const sheet = (
    <div className="srt-how-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="srt-how" role="dialog" aria-modal="true" aria-label={t('sorteiosHowTitle')} onMouseDown={(e) => e.stopPropagation()}>
        <header className="srt-how-head">
          <h2>{t('sorteiosHowTitle')}</h2>
          <button className="srt-how-x" type="button" onClick={onClose} aria-label={t('close')}><X size={18} aria-hidden="true" /></button>
        </header>

        <div className="srt-how-body">
          <ol className="srt-how-steps">
            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">1</span>
              <div className="srt-how-step-body">
                <div className="srt-how-strip" aria-hidden="true">
                  {cell(0, 'is-miss')}{cell(1, 'is-miss')}{cell(2, 'is-miss')}
                  {cell(3, 'is-you', t('sorteiosHowYou'))}
                  <span className="srt-how-cell is-more">…</span>
                </div>
                <p>{t('sorteiosHowS1')}</p>
              </div>
            </li>
            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">2</span>
              <div className="srt-how-step-body">
                <div className="srt-how-chip" aria-hidden="true">
                  <Link2 size={14} aria-hidden="true" />
                  <span>Ethereum</span>
                  <b>D = {exD}</b>
                </div>
                <p>{t('sorteiosHowS2')}</p>
              </div>
            </li>
            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">3</span>
              <div className="srt-how-step-body">
                <div className="srt-how-math" aria-hidden="true">
                  <span>{t('sorteiosHowS3Entries').replace('%n%', String(exN))}</span>
                  <i><ChevronRight size={13} aria-hidden="true" /><span>{t('sorteiosHowS3Rem').replace('%r%', String(exRem))}</span></i>
                  {/* 余数直接就是中奖票号。这里原先写死 `{exRem} + 1 = …`，
                      那一步随票号改成 0 起一起消失（2026-09-24 PM）。 */}
                  <i><ChevronRight size={13} aria-hidden="true" /><span className="is-win">{ticketLabel(exFirst)}</span></i>
                </div>
                <p>{t('sorteiosHowS3')}</p>
              </div>
            </li>
            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">4</span>
              <div className="srt-how-step-body">
                <div className="srt-how-strip" aria-hidden="true">
                  {cell(exFirst, 'is-rank1', t('fairRank1'))}
                  {cell(exFirst + 1, 'is-rank2', t('fairRank2'))}
                  {cell(exFirst + 2, 'is-rank2', t('fairRank2'))}
                  {cell(exFirst + 3, 'is-rank3', t('fairRank3'))}
                  <span className="srt-how-cell is-more">…</span>
                </div>
                <p>{t('sorteiosHowS4')}</p>
              </div>
            </li>
          </ol>

          <button className="srt-how-earn" type="button" onClick={onEarn}>
            <Ticket size={15} aria-hidden="true" />
            <span>{t('sorteiosHowEarnCta')}</span>
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>

        <footer className="srt-how-foot">
          <button className="srt-how-verify" type="button" onClick={onVerify}>
            <ShieldCheck size={15} aria-hidden="true" />
            <span>{t('sorteiosHowVerify')}</span>
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </footer>
      </section>
    </div>
  );
  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
}

// Hero countdown that never reads ambiguously: ≥24h → "1d 15h", ≥1h → "1:39:07",
// <1h → "39m 07s" (so a sub-hour timer can't be misread as 39 hours).
// formatRaffleCountdown now emits the same unit-bearing shape this used to
// hand-roll ("58m 21s"), so the hero and every other countdown read alike.
function heroCountdown(_secsLeft: number, scheduledAt: number | undefined, nowSec: number) {
  return formatRaffleCountdown(scheduledAt, nowSec);
}

function statusLabel(kind: RaffleScheduleChipKind, draw: RaffleScheduleDraw, t: ReturnType<typeof useI18nStore.getState>['t']) {
  switch (kind) {
    case 'open': return t('sorteiosStatusOpen');
    case 'full': return t('sorteiosStatusFull');
    case 'closed': return t('sorteiosStatusClosed');
    case 'drawing': return t('sorteiosStatusDrawing');
    case 'voided': return t('sorteiosStatusVoided');
    case 'unavailable': return t('sorteiosStatusUnavailable');
    default: return t('sorteiosStatusOpensAt').replace('%t%', hhmm(draw.registration_starts));
  }
}
